import {
	ConstrainDimensionsSelector,
	ContainerSelector,
	IsInAuthor,
	IsInPresentationMode,
	IsPreviewGenerator,
} from "../../state/ui";
import { useEffect, useRef, useState } from "react";

import gsap from "gsap";
import parse from "html-react-parser";
import { strings } from "@quickpoint.me/shared";
import styled from "styled-components";
import { useRecoilValue } from "recoil";

const StyledText = styled.div`
  text-align: start;
  will-change: transform;
  display: inline-block;
`;

// current animation will only really work for animating the last word in a paragraph.
const AnimatedWords = ({
	words,
	tapped,
}: { words: string[]; tapped: boolean }) => {
	const container = useRef(null);
	IsInAuthor;
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const isInPresentationMode = useRecoilValue(IsInPresentationMode);
	const isPreviewGenerator = useRecoilValue(IsPreviewGenerator);
	const [wordIdx, setWordIdx] = useState(0);
	const parsedWords: (string | JSX.Element | JSX.Element[])[] = words.map(
		(word) => parse(word),
	);
	const containerSelector = useRecoilValue(ContainerSelector);

	useEffect(() => {
		setWordIdx(0);
	}, []);

	useEffect(() => {
		if (isPreviewGenerator) return;
		// only allow landscape in presentation mode to animate
		if (
			constrainDimensions &&
			containerSelector === strings.css.portraitViewerID
		)
			return;
		if (constrainDimensions && !isInPresentationMode) return;

		if (!tapped) return;
		const tl = gsap.timeline({
			onComplete: () => {
				if (wordIdx < words.length - 1) {
					if (constrainDimensions && !isInPresentationMode) return;
					setWordIdx(wordIdx + 1);
				}
			},
		});

		// get the length of the parsed word but check if it is a react element first
		let wordLength = 0;

		if (typeof parsedWords[wordIdx] === "string") {
			// @ts-ignore
			wordLength = parsedWords[wordIdx].length;
		}

		if (Array.isArray(parsedWords[wordIdx])) {
			wordLength = 2;
		}

		if (typeof parsedWords[wordIdx] === "object") {
			// @ts-ignore
			wordLength = parsedWords[wordIdx].props.children.length;
		}

		const duration = Math.max(0.8, wordLength * 0.1);

		if (wordIdx === 0) {
			tl.set(container.current, { opacity: 1, scale: 1, delay: duration });
			tl.to(container.current, {
				opacity: 0,
				scale: 1.1,
				ease: "slow(0.25, 0.9, true)",
			});
			return;
		}

		tl.set(container.current, { opacity: 0, scale: 0.1 });
		tl.to(container.current, {
			opacity: 1,
			scale: 1,
			ease: "slow(0.25, 0.9)",
			duration,
		});
		if (wordIdx < words.length - 1) {
			tl.to(container.current, {
				delay: 0.5,
				opacity: 0,
				scale: 1.1,
				ease: "slow(0.25, 0.9, true)",
			});
		}
	}, [
		wordIdx,
		tapped,
		isInPresentationMode,
		constrainDimensions,
		containerSelector,
		isPreviewGenerator,
		parsedWords,
		words.length,
	]);

	return (
		<StyledText ref={container} className="animated-word">
			{parsedWords[wordIdx]}
		</StyledText>
	);
};

export default AnimatedWords;
